import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Check, Share2, Instagram, Twitter, Video, Palette, Link as LinkIcon, User } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth, API } from "../App";
import axios from "axios";

/**
 * Growth Tools / Promo Kit
 * -----------------------
 * Pre-written swipe copy + Canva templates so artists can launch their
 * StardomFans page across social media in minutes.
 */
const PromoKit = ({ token }) => {
  const { user } = useAuth();
  const [copiedKey, setCopiedKey] = useState(null);
  const [profileLink, setProfileLink] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/artist/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.artist_id) {
          setProfileLink(`${window.location.origin}/artist/${res.data.artist_id}`);
        }
      } catch {
        // no artist profile yet — user should build one first
      }
    })();
  }, [token]);

  const link = profileLink || `${window.location.origin}/artist/your-page`;
  const artistName = user?.name || "[Your Name]";

  const swipes = [
    {
      key: "ig",
      icon: Instagram,
      color: "#32D583",
      label: "Instagram / Threads / Facebook",
      note: "Focus: Visual & Community",
      copy: `I'm officially moving my inner circle over to @StardomFans! 🚀\n\nSocial media algorithms make it impossible for me to connect with all of you directly. On StardomFans, I'm building a dedicated stage where my true supporters can get early access to my projects, behind-the-scenes content, and exclusive perks I won't post anywhere else.\n\nTap the link in my bio to join the crew and check out the new page! 🔗👇\n\n${link}`,
    },
    {
      key: "x",
      icon: Twitter,
      color: "#60A5FA",
      label: "X / Twitter",
      note: "Focus: Short & Punchy",
      copy: `Tired of algorithms hiding my updates. I just launched my official @StardomFans page! 🎙️✨\n\nIf you want to support my journey directly and unlock unreleased content, private chats, and exclusive perks — this is the place.\n\nJoin the stage: ${link}`,
    },
    {
      key: "tiktok",
      icon: Video,
      color: "#C084FC",
      label: "TikTok / Reels — 15-second video script",
      note: "Talk directly to the camera",
      copy: `Hey guys, quick announcement! I just launched my official digital stage on StardomFans. If you've been wanting a closer look at my creative process, unreleased tracks/art, or just want to chat with me directly, I'm moving all of that behind-the-scenes stuff over there. Hit the link in my bio to check out the page and join the inner circle. See you there!\n\nOn-screen text idea: "My digital stage is now LIVE"\nEnd card: ${link}`,
    },
  ];

  const graphicTemplates = [
    {
      key: "story",
      title: "Now Live — Story Template (1080×1920)",
      body: "Clean bold background · cutout photo of you centered · bold headline \"MY DIGITAL STAGE IS NOW LIVE.\" · blank space at the bottom for an Instagram Link Sticker.",
      canvaHint: "In Canva, search 'story announcement' and swap the photo + link sticker.",
    },
    {
      key: "unlock",
      title: "What You Get — Feed Post (1080×1080)",
      body: "Split screen. Left: sleek phone mockup of your StardomFans profile. Right: bulleted list — Exclusive Content · Early Access · Direct Chat. Header: \"UNLOCK MY INNER CIRCLE\"",
      canvaHint: "Search 'app promo Instagram' in Canva; drop your profile screenshot into the phone mockup.",
    },
  ];

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      toast.success("Copied — paste into your favourite app!");
      setTimeout(() => setCopiedKey(null), 2500);
    }).catch(() => toast.error("Couldn't copy. Long-press to copy manually."));
  };

  return (
    <div className="space-y-8" data-testid="promo-kit-page">
      <header className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#EC008C]/15 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6 text-[#EC008C]" />
        </div>
        <div>
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-1">Growth Tools · Promo Kit</h1>
          <p className="text-[#8A92A0] text-sm max-w-2xl">
            Ready-to-post copy + graphic templates so your first 10 superfans land on your page in minutes. Copy, paste, and share.
          </p>
        </div>
      </header>

      {/* Profile link card */}
      <div className="card p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="promo-kit-link-card">
        <div className="w-11 h-11 rounded-xl bg-[#EC008C]/10 flex items-center justify-center flex-shrink-0">
          <LinkIcon className="w-5 h-5 text-[#EC008C]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wide text-[#8A92A0] mb-1">Your shareable stage link</p>
          <code className="text-white text-sm break-all">{link}</code>
        </div>
        <Button
          onClick={() => copy(link, "link")}
          className="bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-full px-5"
          data-testid="promo-kit-copy-link-btn"
        >
          {copiedKey === "link" ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy Link</>}
        </Button>
      </div>

      {/* Swipe copy */}
      <section>
        <h2 className="font-['Unbounded'] text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Share2 className="w-4 h-4 text-[#EC008C]" /> Social Swipe Copy
        </h2>
        <div className="space-y-4">
          {swipes.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.key} className="card p-5" data-testid={`swipe-${s.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}20` }}>
                      <Icon className="w-5 h-5" style={{ color: s.color }} />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{s.label}</p>
                      <p className="text-xs text-[#8A92A0]">{s.note}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => copy(s.copy, s.key)}
                    variant="outline"
                    className="border-[#1A2030] text-white hover:border-[#EC008C] rounded-full text-xs"
                    data-testid={`swipe-copy-${s.key}`}
                  >
                    {copiedKey === s.key ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
                  </Button>
                </div>
                <pre className="whitespace-pre-wrap text-sm text-[#C0C6D0] bg-[#050810] p-4 rounded-xl border border-[#1A2030] font-sans leading-relaxed">
                  {s.copy}
                </pre>
              </div>
            );
          })}
        </div>
      </section>

      {/* Graphic templates */}
      <section>
        <h2 className="font-['Unbounded'] text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Palette className="w-4 h-4 text-[#EC008C]" /> Launch Graphic Templates
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {graphicTemplates.map((t) => (
            <div key={t.key} className="card p-5" data-testid={`graphic-${t.key}`}>
              <h3 className="text-white font-semibold mb-2">{t.title}</h3>
              <p className="text-sm text-[#8A92A0] mb-3">{t.body}</p>
              <div className="text-xs text-[#EC008C] bg-[#EC008C]/10 rounded-lg px-3 py-2">
                💡 {t.canvaHint}
              </div>
              <a
                href="https://www.canva.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-[#8A92A0] hover:text-[#EC008C] mt-3"
                data-testid={`open-canva-${t.key}`}
              >
                Open Canva <LinkIcon className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Signature line */}
      <div className="card p-4 flex items-center gap-3 text-sm text-[#8A92A0]" data-testid="promo-kit-signature">
        <User className="w-4 h-4 text-[#EC008C]" />
        <span>Personalize with your name — <span className="text-white">{artistName}</span> — and your unique link before posting.</span>
      </div>
    </div>
  );
};

export default PromoKit;
